import { Component, computed, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';
import { PartnerWorkConfigurationService } from '../../../../services/partner-work-configuration.service';
import { finalize } from 'rxjs/operators';
import PartnerWorkConfiguation = Boo.Objects.Work.PartnerWorkConfiguration;
import PartnerTaskTypeAllocation = Boo.Objects.Work.PartnerTaskTypeAllocation;
import SeoWorkTaskType = Boo.Objects.Work.SeoMinute.SeoWorkTaskType;
import PartnerProductSummary = Boo.Objects.Work.PartnerProductSummary;

@Component({
  selector: 'app-components-manager-partner-work-configurations',
  templateUrl: './partner-work-configurations.component.html',
  styleUrls: ['./partner-work-configurations.component.scss']
})
export class PartnerWorkConfigurationsComponent implements OnInit {
  @Input() partner: Boo.Objects.PartnerMinimal;
  @Input() isReadOnly: boolean;
  @ViewChild('newAllocationInput', {static: false}) newAllocationInput: ElementRef;
  @ViewChild('testAllocationInput', {static: false}) testAllocationInput: ElementRef;
  dfd: any;
  math: Math = Math;
  isBoostabilityPartner = false;
  isLoading = false;
  isInitialized = false;
  title = 'Partner Task Type Allocations';
  hours: number[] = [];
  activeTab = 0;
  newAllocationTabIndex = 0;
  testAllocationTabIndex = -1;
  copyAllocationsTabIndex = -2;
  newAllocationHours: number = null;
  testHours: number = null;
  testedHours: number = null;
  isTesting = false;
  taskTypeAllocationsToTest: PartnerTaskTypeAllocation[] = [];
  partnerWorkConfigurations: PartnerWorkConfiguation[]; 
  taskTypes: SeoWorkTaskType[];
  products: PartnerProductSummary[];
  productGroups: ProductGroup[] = [];
  configuredPartners: Boo.Objects.PartnerFragment[] = [];
  selectedPartner: Boo.Objects.PartnerFragment = null;
  unusedAllocations: number[] = [];

  constructor(private partnerWorkConfigService: PartnerWorkConfigurationService) { }

  ngOnInit(): void {
    this.isLoading = true;
    this.isBoostabilityPartner = this.partner.PartnerId === Boo.Objects.Enums.PartnerEnum.Boostability;
    this.title = `Partner Task Type Allocations - ${this.partner.PartnerName}`;
    forkJoin([this.partnerWorkConfigService.get(this.partner.PartnerId),
      this.partnerWorkConfigService.getTaskTypes(this.partner.PartnerId),
      this.partnerWorkConfigService.getProducts(this.partner.PartnerId),
      this.partnerWorkConfigService.getConfiguredPartners()])
      .pipe(finalize(() => { this.isLoading = false; this.isInitialized = true; }))
      .subscribe(([partnerWorkConfigurations, taskTypes, products, configuredPartners]) => {
        this.configuredPartners = configuredPartners;
        this.partnerWorkConfigurations = partnerWorkConfigurations;
        this.taskTypes = taskTypes;
        this.products = products
          .sort((a, b) => b.HourQuantity - a.HourQuantity)
          .sort((a, b) => a.StoreProductName.localeCompare(b.StoreProductName));

        this.hours.push(
          ...Array.from(
            new Set([...this.partnerWorkConfigurations.map(item => item.Hours)])
          )
          .sort((a, b) => a - b)
        );

        this.productGroups = [...new Set(this.products.map(item => item.HourQuantity))]
          .map(x => ({ Hours: x, Products: this.products.filter(y => y.HourQuantity === x) } as ProductGroup))
          .sort((a, b) => a.Hours - b.Hours);

        this.detectUnusedAllocations();

        if ( this.hours.length > 0) {
          this.setActiveTab(this.hours[0]);
        }
      });
  }

  hours2 = computed<number[]>(() => {
    return Array.from(
      new Set([...this.partnerWorkConfigurations.map(item => item.Hours)])
    )
    .sort((a, b) => a - b);
  }, {equal: () => false});

  copyPartnerWorkConfigurations(): void {
    if (!this.selectedPartner) {
      toastr.error('Please select a partner to copy allocations.');
      return;
    }

    // Clear allocations before copying from another partner
    // This ensures that the ui can reload properly
    this.partnerWorkConfigurations = [];
    this.hours = [];
    this.taskTypeAllocationsToTest = [];

    this.isLoading = true;

    this.partnerWorkConfigService.get(this.selectedPartner.PartnerId)
      .pipe(finalize(() => { this.isLoading = false; this.isInitialized = true; }))
      .subscribe((partnerWorkConfigurations) => {
        this.partnerWorkConfigurations = partnerWorkConfigurations.map(partnerWorkConfiguration => {
          partnerWorkConfiguration.PartnerWorkConfigurationId = 0;
          partnerWorkConfiguration.PartnerId = this.partner.PartnerId;
          partnerWorkConfiguration.TaskTypeAllocations = partnerWorkConfiguration.TaskTypeAllocations.map(taskTypeAllocation => {
            taskTypeAllocation.PartnerTaskTypeAllocationId = 0;
            taskTypeAllocation.PartnerWorkConfigurationId = 0;
            return taskTypeAllocation;
          });
          return partnerWorkConfiguration;
        });

        this.hours = [...new Set(this.partnerWorkConfigurations.map(item => item.Hours))];
        this.detectUnusedAllocations();

        toastr.info(`Allocations copied from ${this.selectedPartner.Name}`);
        this.selectedPartner = null;
      }, () => toastr.error(`Allocations could not be copied from ${this.selectedPartner.Name}`));
  }

  close(): void {
    if (this.dfd) {
      this.dfd.resolve();
    }
  }

  save(): void {
    if (this.validate()) {
      this.isLoading = true;

      this.partnerWorkConfigurations.forEach(x => {
        x.TaskTypeAllocations = x.TaskTypeAllocations.filter(y => y.TaskCount > 0);
      });

      this.partnerWorkConfigService.save(this.partner.PartnerId, this.partnerWorkConfigurations)
        .pipe(finalize(() => this.isLoading = false))
        .subscribe(() => {
          toastr.success('Partner task type allocations saved successfully.');
          if (this.dfd) {
            this.dfd.resolve();
          }
        });
    }
  }

  validate(): boolean {
    let isValid = true;
    const invalidAllocations: number[] = [];
    this.hours.forEach(hours => {
      const taskTypeAllocations = this.partnerWorkConfigurations.find(x => x.Hours === hours).TaskTypeAllocations.filter( x => x.TaskCount > 0);
      const allocatedMinutes = taskTypeAllocations.map(item => item.TaskType.BillableMinutes * item.TaskCount).reduce((a, b) => a + b, 0);
      if (allocatedMinutes > (hours * 60)) {
        invalidAllocations.push(hours);
        isValid = false;
      }
    });

    if (!isValid) {
      toastr.error(`Cannot save allocations. The following hours have not been correctly allocated: ${invalidAllocations.join(', ')}`);
      if (invalidAllocations.indexOf(this.activeTab) === -1) {
        this.setActiveTab(invalidAllocations[0]);
      }
    }

    return isValid;
  }

  setActiveTab(index: number): void {
    this.activeTab = index;

    // setTimeout is currently required for this to work well, although another solution is welcome
    switch (index) {
      case this.newAllocationTabIndex:
        setTimeout(() => { this.newAllocationInput.nativeElement.focus(); }, 0);
        break;
      case this.testAllocationTabIndex:
        setTimeout(() => { this.testAllocationInput.nativeElement.focus(); }, 0);
        break;
      default:
    }
  }

  removeHourAllocation(hours: number): void {
    this.partnerWorkConfigurations = this.partnerWorkConfigurations.filter(x => x.Hours !== hours);
    const index = Math.max(0, this.hours.indexOf(hours) - 1);
    this.hours = this.hours.filter(x => x !== hours);
    this.detectUnusedAllocations();
    this.setActiveTab(this.hours[index]);
    toastr.info(`Allocation for ${ hours } ${ hours > 1 ? 'hours' : 'hour'} removed.`);
  }

  removeUnusedAllocation(hours: number): void {
    this.partnerWorkConfigurations = this.partnerWorkConfigurations.filter(x => x.Hours !== hours);
    this.hours = this.hours.filter(x => x !== hours);
    this.detectUnusedAllocations();
    toastr.info(`Allocation for ${ hours } ${ hours > 1 ? 'hours' : 'hour'} removed.`);
  }

  removeAllUnusedAllocations(): void {
    this.partnerWorkConfigurations = this.partnerWorkConfigurations.filter(x => !this.unusedAllocations.includes(x.Hours));
    this.hours = this.hours.filter(x => !this.unusedAllocations.includes(x));
    this.detectUnusedAllocations();
    toastr.info(`All unused allocations removed.`);
  }

  addAllocation(): void {
    if (this.newAllocationHours) {
      this.isLoading = true;
      if (!this.hours.find(x => x === this.newAllocationHours)) {
        this.partnerWorkConfigurations.forEach(x => {
          x.TaskTypeAllocations = x.TaskTypeAllocations.filter(y => y.TaskCount > 0);
        });
        this.partnerWorkConfigService.generatePreview(this.partner.PartnerId, this.newAllocationHours, this.partnerWorkConfigurations)
        .pipe(finalize(() => this.isLoading = false))
        .subscribe((taskTypeAllocations) => {
          this.partnerWorkConfigurations.push(this.generateNewPartnerWorkConfiguration(taskTypeAllocations, this.newAllocationHours));
          this.hours.push(this.newAllocationHours);
          this.hours = this.hours.sort((a, b) => a - b);
          this.detectUnusedAllocations();
          this.setActiveTab(this.newAllocationHours);

          toastr.info(`Allocation generated for ${ this.newAllocationHours } ${ this.newAllocationHours > 1 ? 'hours' : 'hour'}`);
          this.newAllocationHours = null;
        });

        return;
      }
      this.setActiveTab(this.newAllocationHours);
      toastr.info(`An allocation for ${ this.newAllocationHours } ${ this.newAllocationHours > 1 ? 'hours' : 'hour'} exists.`);
      this.newAllocationHours = null;
      this.isLoading = false;
    }
  }

  isAllocationValid(hours: number): boolean {
    const partnerWorkConfiguration = this.partnerWorkConfigurations.find(x => x.Hours === hours);
    
    if(!partnerWorkConfiguration || !partnerWorkConfiguration.TaskTypeAllocations || partnerWorkConfiguration.TaskTypeAllocations.length === 0) {
      return true;
    }

    const allocatedMinutes = partnerWorkConfiguration.TaskTypeAllocations.map(item => item.TaskType.BillableMinutes * item.TaskCount).reduce((a, b) => a + b, 0);
    return allocatedMinutes <= (hours * 60);
  }

  isAllocated(hours: number): boolean {
    return this.hours.indexOf(hours) !== -1;
  }

  unallocatedMinutes(hours: number): number {
    const taskTypeAllocations = this.partnerWorkConfigurations.find(x => x.Hours === hours).TaskTypeAllocations;
    const allocatedMinutes = taskTypeAllocations.length ? taskTypeAllocations.map(item => item.TaskType.BillableMinutes * item.TaskCount).reduce((a, b) => a + b, 0) : 0;
    return Math.max(0, (hours * 60) - allocatedMinutes);
  }

  testAllocation(): void {
    if (this.testHours) {
      this.isTesting = true;
      this.taskTypeAllocationsToTest = [];
      
      this.partnerWorkConfigurations.forEach(x => {
        x.TaskTypeAllocations = x.TaskTypeAllocations.filter(y => y.TaskCount > 0);
      });
      this.partnerWorkConfigService.generatePreview(this.partner.PartnerId, this.testHours, this.partnerWorkConfigurations)
      .pipe(finalize(() => this.isTesting = false))
      .subscribe((taskTypeAllocations) => {
        this.taskTypeAllocationsToTest = taskTypeAllocations.sort((a: PartnerTaskTypeAllocation, b: PartnerTaskTypeAllocation) => a.TaskType.Name.localeCompare(b.TaskType.Name))
          .sort((a: PartnerTaskTypeAllocation, b: PartnerTaskTypeAllocation) => a.TaskType.BillableMinutes - b.TaskType.BillableMinutes);
        this.testedHours = this.testHours;
      });
    }
  }

  addAllocationFromTestResults(): void {
    this.newAllocationHours = this.testedHours;
    this.addAllocation();
  }

  addAllocationFromProductGroup(productGroup: ProductGroup): void {
    this.newAllocationHours = productGroup.Hours;
    this.addAllocation();
  }

  clearResults(): void {
    this.taskTypeAllocationsToTest = [];
  }

  get canAddNewAllocation(): boolean {
    return this.testedHours && !this.hours.find(x => x === this.testedHours);
  }

  getPartnerWorkConfiguration(hour: number): PartnerWorkConfiguation {
    return this.partnerWorkConfigurations.find(x => x.Hours === hour);
  }

  private detectUnusedAllocations(): void {
    this.unusedAllocations = this.hours.filter(hours => !this.productGroups.find(x => x.Hours === hours));
  }

  private generateNewPartnerWorkConfiguration(taskTypeAllocations: PartnerTaskTypeAllocation[], hours: number): PartnerWorkConfiguation {
    return {
      PartnerWorkConfigurationId: 0,
      PartnerId: this.partner.PartnerId,
      Hours: hours,
      Minutes: hours * 60,
      TaskTypeAllocations: taskTypeAllocations.map(taskTypeAllocation => ({
        PartnerTaskTypeAllocationId: 0,
        PartnerWorkConfigurationId: 0,
        TaskTypeId: taskTypeAllocation.TaskTypeId,
        TaskCount: taskTypeAllocation.TaskCount,
        TaskType: taskTypeAllocation.TaskType,
        MinutesPerTask: taskTypeAllocation.MinutesPerTask
      }))
    };
  }
}

class ProductGroup {
  Hours: number;
  Products: PartnerProductSummary[];
}
